import React, { useEffect, useMemo, useState, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import Swal from "sweetalert2";
import { useNavigate } from "react-router-dom";
import Switch from "react-switch";
import "./themeupdate.css";
import { fetchThemeData } from "../../themeSlice";
import { selectAdminToken, selectOrganizationId, selectSessionId } from "../../sessionSlice";
import { updateThemeData } from "../../functions/updateThemeData";
import { s3PublicUrl } from "../../../utils/s3PublicUrl";
import ArrowPuzzleLabelSidebar from "../../../components/ArrowPuzzleLabelSidebar/ArrowPuzzleLabelSidebar";
import ArrowPuzzlePreview from "../../../components/ArrowPuzzlePreview/ArrowPuzzlePreview";
import {
  coerceLabelFontScale,
  collectWordsFromCatItems,
  generateArrowPuzzle,
  getArrowPuzzleForCategory,
  getCategoryKeysWithUsableWords,
  normalizeArrowPuzzle,
  normalizeLabelRotationDeg,
  parseArrowPuzzleValue,
  parseArrowPuzzlesMap,
  parseGridSize,
} from "../../../utils/arrowPuzzleGenerator";

const ADD_CONTENT_KEYS = [
  { key: "points_icon", label: "Points icon", type: "icon" },
  { key: "default_question_sound", label: "Click sound", type: "sound" },
  { key: "click_right_answer_sound", label: "Right answer sound", type: "sound" },
  { key: "click_wrong_answer_sound", label: "Wrong answer sound", type: "sound" },
];
const MAX_MP3_BYTES = 10 * 1024 * 1024;
const ADD_CONTENT_ICON_SIZE = [50, 50];

const MAX_TOTAL_CATEGORIES = 5;
const MAX_DEFAULT_CATEGORIES_SELECTED = 3;
const MAX_CATEGORY_LEN = 30;
/** Non-empty correct + wrong entries together must be at least 2 (≥1 each) and at most this many. */
const MIN_SIDE_CHOICES = 1;
const MAX_COMBINED_CHOICES = 10;
const ICON_SIZE = 128;
const VALID_EXT = ["png", "jpg", "jpeg"];

const ensureArray = (val) => {
  if (Array.isArray(val)) return val;
  if (val == null || val === "") return [];
  if (typeof val === "string") {
    try {
      const p = JSON.parse(val);
      return Array.isArray(p) ? p : [];
    } catch {
      return [];
    }
  }
  return [];
};

const strBool = (v) => v === true || v === "true" || v === "1";

/** Merge legacy single `arrow_puzzle` into per-category map when the map has no entry yet. */
function mergeLegacyArrowIntoMap(map, legacyRaw, puzzleOptsFromData) {
  const leg = parseArrowPuzzleValue(legacyRaw);
  if (!leg?.paths?.length) return { ...map };
  const out = { ...map };
  const ck =
    leg.categoryKey != null && String(leg.categoryKey).trim() ? String(leg.categoryKey).toUpperCase() : null;
  if (ck && puzzleOptsFromData.includes(ck) && !out[ck]) {
    out[ck] = normalizeArrowPuzzle(leg);
  } else if (!ck && Object.keys(out).length === 0 && puzzleOptsFromData[0]) {
    const t0 = puzzleOptsFromData[0];
    out[t0] = normalizeArrowPuzzle({ ...leg, categoryKey: t0 });
  }
  return out;
}

const ensureCatItemsRaw = (val) => {
  if (val && typeof val === "object" && !Array.isArray(val)) return { ...val };
  if (typeof val === "string") {
    try {
      const p = JSON.parse(val);
      return p && typeof p === "object" && !Array.isArray(p) ? { ...p } : {};
    } catch {
      return {};
    }
  }
  return {};
};

const emptyCatItem = () => ({
  heading: "",
  correct: [""],
  wrong: [""],
  info: "",
});

const padMinOne = (arr) => {
  const a = Array.isArray(arr) ? arr.map((x) => (x == null ? "" : String(x))) : [];
  if (a.length === 0) return [""];
  return a;
};

const normalizeCatItem = (raw) => {
  if (!raw || typeof raw !== "object") return emptyCatItem();
  let correct = padMinOne(raw.correct);
  let wrong = padMinOne(raw.wrong);
  while (correct.length + wrong.length > MAX_COMBINED_CHOICES) {
    if (wrong.length > MIN_SIDE_CHOICES) wrong = wrong.slice(0, -1);
    else if (correct.length > MIN_SIDE_CHOICES) correct = correct.slice(0, -1);
    else break;
  }
  return {
    heading: typeof raw.heading === "string" ? raw.heading : "",
    correct,
    wrong,
    info: typeof raw.info === "string" ? raw.info : "",
  };
};

const pickCatItemCaseInsensitive = (itemsRaw, catUpper) => {
  if (!itemsRaw || typeof itemsRaw !== "object") return null;
  const k = String(catUpper || "").trim();
  if (!k) return null;
  return itemsRaw[k] ?? itemsRaw[k.toUpperCase()] ?? itemsRaw[k.toLowerCase()] ?? null;
};

const ThemeUpdate = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const adminToken = useSelector(selectAdminToken);
  const organizationId = useSelector(selectOrganizationId);
  const sessionId = useSelector(selectSessionId);
  const { currentTheme, data, status } = useSelector((state) => state.theme);

  const [activeTab, setActiveTab] = useState("categories");
  const [savingSelection, setSavingSelection] = useState(false);
  const [savingContent, setSavingContent] = useState(false);
  const [uploadingIcon, setUploadingIcon] = useState(false);
  const [gridLoading, setGridLoading] = useState(false);
  const [fullDefaultPool, setFullDefaultPool] = useState([]);
  const [categoryPool, setCategoryPool] = useState([]);
  const [customCat, setCustomCat] = useState([]);
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [categoryToAdd, setCategoryToAdd] = useState("");
  const [newCategoryName, setNewCategoryName] = useState("");
  const [catItems, setCatItems] = useState({});
  const [gridSelected, setGridSelected] = useState("");
  const [wordlessMode, setWordlessMode] = useState(false);
  const [arrowPuzzleEnabled, setArrowPuzzleEnabled] = useState(false);
  /** Uppercase category → saved/generated puzzle (same shape as themeset / DB). */
  const [arrowPuzzlesByCategory, setArrowPuzzlesByCategory] = useState({});
  const [arrowPuzzleLabelEdit, setArrowPuzzleLabelEdit] = useState(false);
  const [arrowPuzzleSelectedLabelIndex, setArrowPuzzleSelectedLabelIndex] = useState(null);
  const [generatingPuzzle, setGeneratingPuzzle] = useState(false);
  /** Uppercase key: puzzle words come only from this category’s cat_items. */
  const [puzzleCategoryKey, setPuzzleCategoryKey] = useState(null);
  const [categoryForImage, setCategoryForImage] = useState("");
  const [uploadCategoryFile, setUploadCategoryFile] = useState(null);

  const [soundToggle, setSoundToggle] = useState("true");
  const [addContentFiles, setAddContentFiles] = useState(() =>
    ADD_CONTENT_KEYS.reduce((acc, { key }) => ({ ...acc, [key]: null }), {})
  );
  const [addContentPreviews, setAddContentPreviews] = useState(() =>
    ADD_CONTENT_KEYS.reduce((acc, { key }) => ({ ...acc, [key]: null }), {})
  );
  const [mediaUploading, setMediaUploading] = useState(false);
  const fileInputRefs = useRef({});

  useEffect(() => {
    if (!data) return;
    const s3 = process.env.REACT_APP_S3_PATH || "";
    const initial = {};
    ADD_CONTENT_KEYS.forEach(({ key }) => {
      const path = data[key];
      initial[key] = path && String(path).trim() !== "" ? `${s3}${path}` : null;
    });
    setAddContentPreviews((prev) => ({ ...prev, ...initial }));
    if (data.sound_toggle != null) {
      setSoundToggle(String(data.sound_toggle));
    }
  }, [data]);

  useEffect(() => {
    return () => {
      Object.values(addContentPreviews).forEach((url) => {
        if (url && url.startsWith("blob:")) URL.revokeObjectURL(url);
      });
    };
  }, []);

  const validateMediaImage = (file) => {
    const ext = file.name.split(".").pop().toLowerCase();
    if (ext !== "png") return "Only PNG images are allowed.";
    if (file.type !== "image/png") return "Only PNG images are allowed.";
    return null;
  };

  const validateMediaMp3 = (file) => {
    const ext = file.name.split(".").pop().toLowerCase();
    if (ext !== "mp3") return "Only MP3 files are allowed.";
    const type = (file.type || "").toLowerCase();
    if (type && type !== "application/octet-stream" && !type.startsWith("audio/")) return "Invalid MP3 file.";
    if (file.size > MAX_MP3_BYTES) return "MP3 must be 10 MB or smaller.";
    return null;
  };

  const getMediaImageDimensions = (file) =>
    new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => {
        resolve({ width: img.width, height: img.height });
        URL.revokeObjectURL(img.src);
      };
      img.onerror = () => reject(new Error("Invalid image"));
      img.src = URL.createObjectURL(file);
    });

  const handleMediaFileChange = async (itemDef, e) => {
    const { key, type, label } = itemDef;
    const file = e.target.files?.[0];
    if (!file) return;

    if (type === "icon") {
      const typeError = validateMediaImage(file);
      if (typeError) {
        Swal.fire("Error", typeError, "error");
        e.target.value = "";
        return;
      }
      try {
        const { width, height } = await getMediaImageDimensions(file);
        if (width !== ADD_CONTENT_ICON_SIZE[0] || height !== ADD_CONTENT_ICON_SIZE[1]) {
          Swal.fire("Error", `Icons must be exactly ${ADD_CONTENT_ICON_SIZE[0]}×${ADD_CONTENT_ICON_SIZE[1]} pixels. This image is ${width}×${height}.`, "error");
          e.target.value = "";
          return;
        }
      } catch {
        Swal.fire("Error", "Could not read this image.", "error");
        e.target.value = "";
        return;
      }
    } else if (type === "sound") {
      const err = validateMediaMp3(file);
      if (err) {
        Swal.fire("Error", err, "error");
        e.target.value = "";
        return;
      }
    }

    setAddContentFiles((prev) => ({ ...prev, [key]: file }));
    setAddContentPreviews((prev) => {
      if (prev[key] && prev[key].startsWith("blob:")) URL.revokeObjectURL(prev[key]);
      return { ...prev, [key]: URL.createObjectURL(file) };
    });
    e.target.value = "";
  };

  const handleUploadAddContent = async () => {
    const hasFile = Object.values(addContentFiles).some(Boolean);
    if (!hasFile && String(soundToggle) === String(data?.sound_toggle)) {
      Swal.fire("Info", "No changes to upload.", "info");
      return;
    }

    setMediaUploading(true);
    try {
      const formData = new FormData();
      formData.append("themeName", currentTheme);
      formData.append("organizationId", organizationId || "admin");
      formData.append("sessionId", sessionId || "admin");
      formData.append("extraData", JSON.stringify({ sound_toggle: soundToggle }));

      ADD_CONTENT_KEYS.forEach(({ key }) => {
        if (addContentFiles[key]) formData.append(key, addContentFiles[key]);
      });

      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/updateThemeMedia`, {
        method: "POST",
        headers: { Authorization: `Bearer ${adminToken}` },
        body: formData,
      });

      if (!res.ok) throw new Error("Upload failed");
      Swal.fire("Success", "Saved successfully.", "success");
      dispatch(fetchThemeData({ themeId: currentTheme, isAdmin: true }));
      setAddContentFiles(ADD_CONTENT_KEYS.reduce((acc, { key }) => ({ ...acc, [key]: null }), {}));
    } catch (err) {
      Swal.fire("Error", err.message || "Upload failed.", "error");
    } finally {
      setMediaUploading(false);
    }
  };

  useEffect(() => {
    dispatch(fetchThemeData({ themeId: currentTheme || null, isAdmin: true }));
  }, [dispatch, currentTheme]);

  useEffect(() => {
    if (!data) return;
    const full = ensureArray(data.full_default_cat).map((c) => String(c).toUpperCase());
    const active = ensureArray(data.default_cat).map((c) => String(c).toUpperCase());
    const custom = ensureArray(data.custom_cat).map((c) => String(c).toUpperCase());
    const iconKeys =
      data.cat_icons && typeof data.cat_icons === "object" && !Array.isArray(data.cat_icons)
        ? Object.keys(data.cat_icons).map((k) => String(k).toUpperCase())
        : [];
    const poolBase = full.length ? full : active;
    const pool = [...new Set([...poolBase, ...custom, ...iconKeys].filter(Boolean))];
    setFullDefaultPool([...poolBase]);
    setCategoryPool(pool);
    setCustomCat(custom);
    setSelectedCategories(active.length ? active : poolBase.length ? [...poolBase] : []);

    const fromDb = ensureCatItemsRaw(data.cat_items);
    const merged = {};
    pool.forEach((cat) => {
      merged[cat] = normalizeCatItem(pickCatItemCaseInsensitive(fromDb, cat));
    });
    setCatItems(merged);

    const nextSelected = active.length ? active : poolBase.length ? [...poolBase] : [];
    const puzzleOptsFromData = [...new Set(nextSelected.map((c) => String(c).toUpperCase()))];
    const puzzleMap = mergeLegacyArrowIntoMap(
      parseArrowPuzzlesMap(data.arrow_puzzles),
      data.arrow_puzzle,
      puzzleOptsFromData
    );
    setArrowPuzzlesByCategory(puzzleMap);

    setPuzzleCategoryKey((prev) => {
      if (prev && puzzleOptsFromData.includes(prev)) return prev;
      const withPuzzle = puzzleOptsFromData.find((c) => puzzleMap[c]?.paths?.length);
      return withPuzzle ?? puzzleOptsFromData[0] ?? null;
    });

    const opts = ensureArray(data.grid_options);
    const optStrs = opts.map(String);
    let gs = data.grid_selected != null ? String(data.grid_selected) : "";
    const anchorTab = puzzleOptsFromData.find((c) => puzzleMap[c]?.paths?.length);
    const anchorPuzzle = anchorTab ? puzzleMap[anchorTab] : null;
    if (anchorPuzzle && Number(anchorPuzzle.width) > 0 && Number(anchorPuzzle.height) > 0) {
      const puzzleOpt = `${Number(anchorPuzzle.width)}x${Number(anchorPuzzle.height)}`;
      if (optStrs.includes(puzzleOpt)) gs = puzzleOpt;
    }
    if (opts.length && !optStrs.includes(gs)) gs = String(opts[0]);
    setGridSelected(gs);

    setWordlessMode(strBool(data.wordless_mode));
    setArrowPuzzleEnabled(strBool(data.arrow_puzzle_enabled));
  }, [data]);

  useEffect(() => {
    if (!puzzleCategoryKey || !data) return;
    const p = arrowPuzzlesByCategory[puzzleCategoryKey];
    if (!p?.width || !p?.height) return;
    const pk = `${Number(p.width)}x${Number(p.height)}`;
    const opts = ensureArray(data.grid_options).map(String);
    if (opts.includes(pk)) setGridSelected(pk);
  }, [puzzleCategoryKey, arrowPuzzlesByCategory, data]);

  useEffect(() => {
    if (!arrowPuzzleLabelEdit) setArrowPuzzleSelectedLabelIndex(null);
  }, [arrowPuzzleLabelEdit]);

  // When wordless mode is turned off, disable edit mode (preview hides labels then).
  useEffect(() => {
    if (!wordlessMode && arrowPuzzleLabelEdit) {
      setArrowPuzzleLabelEdit(false);
    }
  }, [wordlessMode, arrowPuzzleLabelEdit]);

  /** Category content editors only for chips currently selected on the Categories tab. */
  const categoriesForContent = useMemo(
    () => [...new Set(selectedCategories.map((c) => String(c).toUpperCase()))],
    [selectedCategories]
  );

  const puzzleCatOptions = useMemo(
    () => [...new Set(selectedCategories.map((c) => String(c).toUpperCase()))],
    [selectedCategories]
  );

  const puzzleForPreview = useMemo(() => {
    if (!puzzleCategoryKey) return null;
    const local = arrowPuzzlesByCategory[puzzleCategoryKey];
    if (local?.paths?.length) return local;
    return getArrowPuzzleForCategory(data, puzzleCategoryKey) ?? null;
  }, [arrowPuzzlesByCategory, puzzleCategoryKey, data]);

  const activePuzzleGeneratedAt = useMemo(() => {
    if (!puzzleCategoryKey) return null;
    return arrowPuzzlesByCategory[puzzleCategoryKey]?.generatedAt ?? null;
  }, [puzzleCategoryKey, arrowPuzzlesByCategory]);

  useEffect(() => {
    setArrowPuzzleSelectedLabelIndex(null);
  }, [puzzleCategoryKey, activePuzzleGeneratedAt]);

  useEffect(() => {
    setPuzzleCategoryKey((prev) => {
      if (prev && puzzleCatOptions.includes(prev)) return prev;
      return puzzleCatOptions[0] ?? null;
    });
  }, [puzzleCatOptions]);

  useEffect(() => {
    if (categoryForImage && !selectedCategories.includes(categoryForImage)) {
      setCategoryForImage("");
    }
  }, [selectedCategories, categoryForImage]);

  useEffect(() => {
    setCatItems((prev) => {
      let changed = false;
      const next = { ...prev };
      selectedCategories.forEach((cat) => {
        const k = String(cat).toUpperCase();
        if (next[k] == null) {
          next[k] = normalizeCatItem(null);
          changed = true;
        }
      });
      return changed ? next : prev;
    });
  }, [selectedCategories]);

  const isDefaultCategory = (cat) => fullDefaultPool.includes(String(cat).toUpperCase());

  const countDefaultSelected = () =>
    selectedCategories.filter((c) => isDefaultCategory(c)).length;

  const updateCatItem = (cat, partial) => {
    setCatItems((prev) => ({
      ...prev,
      [cat]: { ...normalizeCatItem(prev[cat]), ...partial },
    }));
  };

  const updateListCell = (cat, field, index, value) => {
    setCatItems((prev) => {
      const cur = normalizeCatItem(prev[cat]);
      const arr = [...cur[field]];
      arr[index] = value;
      return { ...prev, [cat]: { ...cur, [field]: arr } };
    });
  };

  const addListRow = (cat, field) => {
    setCatItems((prev) => {
      const cur = normalizeCatItem(prev[cat]);
      if (cur.correct.length + cur.wrong.length >= MAX_COMBINED_CHOICES) return prev;
      const arr = [...cur[field]];
      arr.push("");
      return { ...prev, [cat]: { ...cur, [field]: arr } };
    });
  };

  const removeListRow = (cat, field, index) => {
    setCatItems((prev) => {
      const cur = normalizeCatItem(prev[cat]);
      const arr = [...cur[field]];
      if (arr.length <= MIN_SIDE_CHOICES) return prev;
      arr.splice(index, 1);
      return { ...prev, [cat]: { ...cur, [field]: arr } };
    });
  };

  const validateCatItemsForSave = (poolKeys, itemsMap, keysToValidate = null) => {
    const keys =
      keysToValidate != null
        ? [...new Set(keysToValidate.map((k) => String(k).toUpperCase()))]
        : poolKeys;
    for (const cat of keys) {
      const item = normalizeCatItem(itemsMap[cat]);
      const correctRaw = item.correct.map((s) => String(s).trim());
      const wrongRaw = item.wrong.map((s) => String(s).trim());
      // If the admin added extra rows, they cannot be left empty (prevents "saving empty content").
      if (correctRaw.some((s) => s === "")) {
        Swal.fire("Error", `Category "${cat}": correct choices cannot be empty.`, "error");
        return false;
      }
      if (wrongRaw.some((s) => s === "")) {
        Swal.fire("Error", `Category "${cat}": incorrect choices cannot be empty.`, "error");
        return false;
      }
      const correct = correctRaw.filter(Boolean);
      const wrong = wrongRaw.filter(Boolean);
      if (!item.heading.trim()) {
        Swal.fire("Error", `Category "${cat}": heading is required.`, "error");
        return false;
      }
      if (item.heading.trim().length > 150) {
        Swal.fire("Error", `Category "${cat}": heading must be at most 150 characters.`, "error");
        return false;
      }
      if (correct.length < MIN_SIDE_CHOICES) {
        Swal.fire("Error", `Category "${cat}": at least ${MIN_SIDE_CHOICES} non-empty correct choice.`, "error");
        return false;
      }
      if (correct.some((s) => s.length > 20)) {
        Swal.fire("Error", `Category "${cat}": correct choices must be at most 20 characters each.`, "error");
        return false;
      }
      if (wrong.length < MIN_SIDE_CHOICES) {
        Swal.fire("Error", `Category "${cat}": at least ${MIN_SIDE_CHOICES} non-empty incorrect choice.`, "error");
        return false;
      }
      if (wrong.some((s) => s.length > 20)) {
        Swal.fire("Error", `Category "${cat}": incorrect choices must be at most 20 characters each.`, "error");
        return false;
      }
      const combined = correct.length + wrong.length;
      if (combined > MAX_COMBINED_CHOICES) {
        Swal.fire(
          "Error",
          `Category "${cat}": correct and incorrect choices together must be at most ${MAX_COMBINED_CHOICES} (you have ${combined}).`,
          "error"
        );
        return false;
      }
      if (!item.info.trim()) {
        Swal.fire("Error", `Category "${cat}": info text is required.`, "error");
        return false;
      }
      if (item.info.trim().length > 400) {
        Swal.fire("Error", `Category "${cat}": info text must be at most 400 characters.`, "error");
        return false;
      }
    }
    return true;
  };

  const buildCatItemsPayloadFrom = (poolKeys, itemsMap) => {
    const out = {};
    poolKeys.forEach((k) => {
      const item = normalizeCatItem(itemsMap[k]);
      out[k] = {
        heading: item.heading.trim(),
        correct: item.correct.map((s) => String(s).trim()).filter(Boolean),
        wrong: item.wrong.map((s) => String(s).trim()).filter(Boolean),
        info: item.info.trim(),
      };
    });
    return out;
  };

  const handleAddCustomCategory = async () => {
    const newCat = newCategoryName.trim().toUpperCase();
    if (!newCat) return;

    if (newCat.length > MAX_CATEGORY_LEN) {
      Swal.fire("Error", "Maximum category length reached (30).", "error");
      return;
    }
    let custom = [...customCat].map((c) => String(c).toUpperCase());
    if (custom.includes(newCat) || categoryPool.includes(newCat)) {
      Swal.fire("Error", "Category already exists.", "error");
      return;
    }

    custom.push(newCat);
    setSavingSelection(true);
    try {
      await updateThemeData({
        payload: {
          data: { custom_cat: custom },
          organizationId: organizationId || "admin",
          sessionId: sessionId || "admin",
          currentTheme,
        },
        token: adminToken,
      });
      await dispatch(fetchThemeData({ themeId: currentTheme, isAdmin: true })).unwrap();
      Swal.fire("Success", "Custom category added! You can now select it.", "success");
      setNewCategoryName("");
    } catch (e) {
      Swal.fire("Error", e.message || "Failed to add custom category", "error");
    } finally {
      setSavingSelection(false);
    }
  };

  const handleSaveCategorySelection = async () => {
    let vars = selectedCategories.filter((c) => c !== "all").map((c) => String(c).toUpperCase());
    let custom = [...customCat].map((c) => String(c).toUpperCase());
    if (vars.length < 1) {
      Swal.fire("Error", "Select at least one category.", "error");
      return;
    }
    if (vars.length > MAX_TOTAL_CATEGORIES) {
      Swal.fire("Error", `You can have at most ${MAX_TOTAL_CATEGORIES} categories.`, "error");
      return;
    }
    const defSel = vars.filter((c) => isDefaultCategory(c));
    if (defSel.length > MAX_DEFAULT_CATEGORIES_SELECTED) {
      Swal.fire(
        "Error",
        `At most ${MAX_DEFAULT_CATEGORIES_SELECTED} default (themeset) categories can be selected.`,
        "error"
      );
      return;
    }
    setSavingSelection(true);
    try {
      await updateThemeData({
        payload: {
          data: {
            default_cat: vars,
            custom_cat: custom,
          },
          organizationId: organizationId || "admin",
          sessionId: sessionId || "admin",
          currentTheme,
        },
        token: adminToken,
      });
      await dispatch(fetchThemeData({ themeId: currentTheme, isAdmin: true })).unwrap();
      Swal.fire("Success", "Category selection saved. Add Category content to see it in the game.", "success");
      setNewCategoryName("");
    } catch (e) {
      Swal.fire("Error", e.message || "Failed to save", "error");
    } finally {
      setSavingSelection(false);
    }
  };

  const handleSaveCategoryContent = async () => {
    const customUpper = customCat.map((c) => String(c).toUpperCase());
    const selectedKeys = [...new Set(selectedCategories.map((c) => String(c).toUpperCase()))];
    const poolKeys = [...new Set([...categoryPool, ...customUpper, ...selectedKeys])];
    const itemsMerged = { ...catItems };
    selectedKeys.forEach((k) => {
      if (itemsMerged[k] == null) itemsMerged[k] = normalizeCatItem(null);
    });

    if (!validateCatItemsForSave(poolKeys, itemsMerged, selectedKeys)) return;

    setSavingContent(true);
    try {
      await updateThemeData({
        payload: {
          data: {
            cat_items: buildCatItemsPayloadFrom(poolKeys, itemsMerged),
          },
          organizationId: organizationId || "admin",
          sessionId: sessionId || "admin",
          currentTheme,
        },
        token: adminToken,
      });
      await dispatch(fetchThemeData({ themeId: currentTheme, isAdmin: true })).unwrap();
      Swal.fire("Success", "Category content saved.", "success");
    } catch (e) {
      Swal.fire("Error", e.message || "Failed to save", "error");
    } finally {
      setSavingContent(false);
    }
  };

  const validateImageDimensions = (file) =>
    new Promise((resolve) => {
      const img = new Image();
      img.onload = () => resolve(img.width === 600 && img.height === 400);
      img.onerror = () => resolve(false);
      img.src = URL.createObjectURL(file);
    });

  const handleUploadCategoryIcon = async (e) => {
    e.preventDefault();
    const file = uploadCategoryFile;
    if (!file || !categoryForImage) {
      Swal.fire("Error", "Select a category and choose a file.", "error");
      return;
    }
    const ext = file.name.split(".").pop().toLowerCase();
    if (!VALID_EXT.includes(ext)) {
      Swal.fire("Error", "Unsupported file format. Use PNG, JPG, JPEG.", "error");
      return;
    }
    const valid = await validateImageDimensions(file);
    if (!valid) {
      Swal.fire("Error", "Icon image size must be 600×400.", "error");
      return;
    }
    const formData = new FormData();
    formData.append("file", file);
    formData.append("current_cat", categoryForImage);
    formData.append("currentTheme", currentTheme);
    setUploadingIcon(true);
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/uploadCategoryImage`, {
        method: "POST",
        headers: { Authorization: `Bearer ${adminToken}` },
        body: formData,
      });
      const result = await res.json().catch(() => ({}));
      if (result.success) {
        Swal.fire("Success", "Category icon uploaded.", "success");
        dispatch(fetchThemeData({ themeId: currentTheme, isAdmin: true }));
        setUploadCategoryFile(null);
      } else {
        Swal.fire("Error", result.error || "Upload failed", "error");
      }
    } catch {
      Swal.fire("Error", "Upload failed", "error");
    } finally {
      setUploadingIcon(false);
    }
  };

  const handleSaveGrid = async () => {
    setGridLoading(true);
    const keepLabelEdit = arrowPuzzleLabelEdit;
    try {
      if (arrowPuzzleEnabled) {
        const { width, height } = parseGridSize(gridSelected);
        const pk = puzzleCategoryKey ? String(puzzleCategoryKey).toUpperCase() : null;
        const p = pk ? arrowPuzzlesByCategory?.[pk] : null;
        const hasPuzzle = p && Array.isArray(p.paths) && p.paths.length > 0;
        const sizeOk = hasPuzzle && Number(p.width) === Number(width) && Number(p.height) === Number(height);
        if (!pk || !hasPuzzle || !sizeOk) {
          Swal.fire(
            "Reminder",
            "You changed the grid size. Please click “Generate puzzle” (and then Save) to apply the new dimensions.",
            "info"
          );
          return;
        }
      }
      const payloadData = {
        grid_selected: gridSelected,
        wordless_mode: wordlessMode ? "true" : "false",
        arrow_puzzle_enabled: arrowPuzzleEnabled ? "true" : "false",
      };
      const cleaned = {};
      for (const [k, v] of Object.entries(arrowPuzzlesByCategory)) {
        if (v && typeof v === "object" && Array.isArray(v.paths) && v.paths.length) {
          const stamped = { ...v, wordless_mode: Boolean(wordlessMode) };
          cleaned[String(k).toUpperCase()] = normalizeArrowPuzzle(stamped);
        }
      }
      const serverMap = parseArrowPuzzlesMap(data?.arrow_puzzles);
      const mergedMap = { ...serverMap, ...cleaned };
      if (Object.keys(mergedMap).length) {
        payloadData.arrow_puzzles = mergedMap;
        const keys = Object.keys(mergedMap).sort();
        payloadData.arrow_puzzle =
          puzzleCategoryKey && mergedMap[puzzleCategoryKey]
            ? mergedMap[puzzleCategoryKey]
            : mergedMap[keys[0]];
      }
      await updateThemeData({
        payload: {
          data: payloadData,
          organizationId: organizationId || "admin",
          sessionId: sessionId || "admin",
          currentTheme,
        },
        token: adminToken,
      });
      const fetchResult = await dispatch(fetchThemeData({ themeId: currentTheme, isAdmin: true })).unwrap();
      const fresh = fetchResult?.data;
      if (fresh) {
        // Preserve the edit toggle across a save/refresh only when wordless is ON.
        setArrowPuzzleLabelEdit(keepLabelEdit && strBool(fresh.wordless_mode));
        const nextSel = selectedCategories.map((c) => String(c).toUpperCase());
        const puzzleOpts = [...new Set(nextSel)];
        const puzzleMap = mergeLegacyArrowIntoMap(
          parseArrowPuzzlesMap(fresh.arrow_puzzles),
          fresh.arrow_puzzle,
          puzzleOpts
        );
        setArrowPuzzlesByCategory(puzzleMap);
        const opts = ensureArray(fresh.grid_options);
        const optStrs = opts.map(String);
        let gs = fresh.grid_selected != null ? String(fresh.grid_selected) : "";
        const anchorTab = puzzleOpts.find((c) => puzzleMap[c]?.paths?.length);
        const anchorPuzzle = anchorTab ? puzzleMap[anchorTab] : null;
        if (anchorPuzzle && Number(anchorPuzzle.width) > 0 && Number(anchorPuzzle.height) > 0) {
          const po = `${Number(anchorPuzzle.width)}x${Number(anchorPuzzle.height)}`;
          if (optStrs.includes(po)) gs = po;
        }
        if (opts.length && !optStrs.includes(gs)) gs = String(opts[0]);
        setGridSelected(gs);
      }
      Swal.fire("Success", "Grid and arrow puzzle settings saved.", "success");
    } catch (e) {
      Swal.fire("Error", e.message || "Failed to save", "error");
    } finally {
      setGridLoading(false);
    }
  };

  const handleGenerateArrowPuzzle = () => {
    if (!arrowPuzzleEnabled) {
      Swal.fire("Info", "Turn on “Arrow puzzle” first.", "info");
      return;
    }
    if (!puzzleCategoryKey) {
      Swal.fire(
        "Error",
        "Choose a puzzle category (tab) with words on the Category content tab: at least one correct and one incorrect choice.",
        "error"
      );
      return;
    }
    const { width, height } = parseGridSize(gridSelected);

    // Always pull words from Category content if available.
    // Even when `wordlessMode` is ON (theme hides labels in-game), we still generate with words so admin preview matches.
    const pooled = collectWordsFromCatItems(catItems, [puzzleCategoryKey]);
    const correctWords = pooled.correct;
    const wrongWords = pooled.wrong;
    const hasWords = correctWords.length > 0 && wrongWords.length > 0;
    if (!hasWords && !wordlessMode) {
      Swal.fire(
        "Error",
        "That category needs at least one non-empty correct word and one non-empty incorrect word (Category content tab).",
        "error"
      );
      return;
    }
    setGeneratingPuzzle(true);
    try {
      const puzzle = generateArrowPuzzle({
        width,
        height,
        correctWords,
        wrongWords,
        wordless: !hasWords && wordlessMode,
        categoryKey: puzzleCategoryKey,
      });
      // Preserve the theme flag even if we generated with words for preview.
      puzzle.wordless_mode = Boolean(wordlessMode);
      setArrowPuzzlesByCategory((prev) => ({
        ...prev,
        [puzzleCategoryKey]: normalizeArrowPuzzle(puzzle),
      }));
    } catch (err) {
      Swal.fire("Error", err?.message || "Could not generate puzzle.", "error");
    } finally {
      setGeneratingPuzzle(false);
    }
  };

  // When Wordless mode is turned OFF, ensure the selected puzzle has real word labels (not C1/W1 placeholders).
  useEffect(() => {
    if (wordlessMode) return;
    if (!puzzleCategoryKey) return;
    const pooled = collectWordsFromCatItems(catItems, [puzzleCategoryKey]);
    const correctWords = pooled.correct.map((x) => String(x.word || "").trim()).filter(Boolean);
    const wrongWords = pooled.wrong.map((x) => String(x.word || "").trim()).filter(Boolean);
    if (correctWords.length === 0 || wrongWords.length === 0) return;

    setArrowPuzzlesByCategory((prev) => {
      const base = prev[puzzleCategoryKey];
      if (!base?.paths?.length) return prev;

      const looksPlaceholder = (lbl) => {
        const t = String(lbl || "").trim().toUpperCase();
        return /^C\d+$/.test(t) || /^W\d+$/.test(t);
      };

      let ci = 0;
      let wi = 0;
      let changed = false;
      const nextPaths = base.paths.map((p) => {
        if (!p || (p.kind !== "correct" && p.kind !== "wrong")) return p;
        const cur = String(p.label || "").trim();
        const shouldReplace = !cur || looksPlaceholder(cur);
        if (!shouldReplace) return p;
        if (p.kind === "correct") {
          const w = correctWords[ci % correctWords.length];
          ci++;
          changed = true;
          return { ...p, label: w };
        }
        const w = wrongWords[wi % wrongWords.length];
        wi++;
        changed = true;
        return { ...p, label: w };
      });

      if (!changed) return prev;
      return { ...prev, [puzzleCategoryKey]: { ...base, wordless_mode: false, paths: nextPaths } };
    });
  }, [wordlessMode, puzzleCategoryKey, catItems]);

  const handlePathLabelLayout = (pathIndex, partial) => {
    if (!puzzleCategoryKey) return;
    setArrowPuzzlesByCategory((prev) => {
      let base = prev[puzzleCategoryKey];
      if (!base?.paths) base = getArrowPuzzleForCategory(data, puzzleCategoryKey);
      if (!base?.paths || base.paths[pathIndex] == null) return prev;
      return {
        ...prev,
        [puzzleCategoryKey]: {
          ...base,
          paths: base.paths.map((p, i) => {
            if (i !== pathIndex) return p;
            const next = { ...p, ...partial };
            if (partial.labelFontScale != null) {
              const s = Number(partial.labelFontScale);
              next.labelFontScale = Number.isNaN(s) ? 1 : coerceLabelFontScale(s);
            }
            if (partial.labelRotationDeg != null) {
              next.labelRotationDeg = normalizeLabelRotationDeg(partial.labelRotationDeg);
            }
            return next;
          }),
        },
      };
    });
  };

  const handleResetLabelPositions = () => {
    if (!puzzleCategoryKey) return;
    setArrowPuzzlesByCategory((prev) => {
      let base = prev[puzzleCategoryKey];
      if (!base?.paths) base = getArrowPuzzleForCategory(data, puzzleCategoryKey);
      if (!base?.paths) return prev;
      return {
        ...prev,
        [puzzleCategoryKey]: {
          ...base,
          paths: base.paths.map((p) => {
            const next = { ...p, labelDxCells: 0, labelDyCells: 0, labelFontScale: 1, labelRotationDeg: 0 };
            delete next.displayLabel;
            return next;
          }),
        },
      };
    });
  };

  const handleResetOnePathLabel = (pathIndex) => {
    setArrowPuzzleSelectedLabelIndex((cur) => (cur === pathIndex ? null : cur));
    if (!puzzleCategoryKey) return;
    setArrowPuzzlesByCategory((prev) => {
      let base = prev[puzzleCategoryKey];
      if (!base?.paths) base = getArrowPuzzleForCategory(data, puzzleCategoryKey);
      if (!base?.paths || base.paths[pathIndex] == null) return prev;
      return {
        ...prev,
        [puzzleCategoryKey]: {
          ...base,
          paths: base.paths.map((p, i) => {
            if (i !== pathIndex) return p;
            const next = { ...p, labelDxCells: 0, labelDyCells: 0, labelFontScale: 1, labelRotationDeg: 0 };
            delete next.displayLabel;
            return next;
          }),
        },
      };
    });
  };

  if (status === "loading" && !data) {
    return (
      <div className="themeupdate-page">
        <div className="themeupdate-loading">Loading...</div>
      </div>
    );
  }

  const catIconCount =
    data?.cat_icons && typeof data.cat_icons === "object" && !Array.isArray(data.cat_icons)
      ? Object.keys(data.cat_icons).length
      : 0;
  const categoryEnabled =
    ensureArray(data?.full_default_cat).length > 0 ||
    ensureArray(data?.default_cat).length > 0 ||
    catIconCount > 0 ||
    data?.catogery === "true" ||
    data?.catogery === true;

  const gridOptions = ensureArray(data?.grid_options);
  const showTabs = Boolean(data);
  const previewCellSize =
    (puzzleForPreview && Number(puzzleForPreview.width) >= 15) || String(gridSelected).includes("15") ? 8 : 11;

  return (
    <div className="themeupdate-page">
      <div className="back-button-holder">
        <button type="button" className="back-button" onClick={() => navigate("/admin/rules")}>
          <i className="fa-solid fa-arrow-left"></i> Back
        </button>
      </div>

      {showTabs && (
        <nav className="themeupdate-tabs" aria-label="Theme update sections">
          <button
            type="button"
            className={`themeupdate-tab ${activeTab === "categories" ? "themeupdate-tab--active" : ""}`}
            onClick={() => setActiveTab("categories")}
          >
            Add Categories
          </button>
          <button
            type="button"
            className={`themeupdate-tab ${activeTab === "content" ? "themeupdate-tab--active" : ""}`}
            onClick={() => setActiveTab("content")}
          >
            Category content
          </button>
          <button
            type="button"
            className={`themeupdate-tab ${activeTab === "grid" ? "themeupdate-tab--active" : ""}`}
            onClick={() => setActiveTab("grid")}
          >
            Grid
          </button>
          <button
            type="button"
            className={`themeupdate-tab ${activeTab === "addcontent" ? "themeupdate-tab--active" : ""}`}
            onClick={() => setActiveTab("addcontent")}
          >
            Others
          </button>
        </nav>
      )}

      {activeTab === "categories" && (
        <div className="themeupdate-panel">
          {categoryEnabled ? (
            <div className="themeupdate-row themeupdate-two-cols">
              {/* Left Column: Selection and Upload */}
              <div className="themeupdate-col-left">
                <div className="themeupdate-card-full">
                  <div className="themeupdate-card-header">Select / Deselect Categories</div>
                  <div className="themeupdate-card-body">
                    <p className="themeupdate-label themeupdate-label-strong">Selected categories:</p>
                    <div className="themeupdate-selected-box">
                      <div className="themeupdate-selected-box-inner">
                        <select
                          className="themeupdate-input themeupdate-select-inline"
                          value={categoryToAdd}
                          onChange={(e) => {
                            const val = e.target.value;
                            if (!val) return;
                            if (selectedCategories.length >= MAX_TOTAL_CATEGORIES) {
                              Swal.fire("Error", `Maximum ${MAX_TOTAL_CATEGORIES} categories.`, "error");
                              e.target.value = "";
                              return;
                            }
                            if (isDefaultCategory(val) && countDefaultSelected() >= MAX_DEFAULT_CATEGORIES_SELECTED) {
                              Swal.fire(
                                "Error",
                                `At most ${MAX_DEFAULT_CATEGORIES_SELECTED} default categories can be selected.`,
                                "error"
                              );
                              e.target.value = "";
                              return;
                            }
                            setSelectedCategories((prev) => [...prev, val]);
                            setCategoryToAdd("");
                          }}
                        >
                          <option value="">Add category...</option>
                          {categoryPool
                            .filter((c) => !selectedCategories.includes(c))
                            .map((cat) => (
                              <option key={cat} value={cat}>
                                {cat}
                              </option>
                            ))}
                        </select>
                      </div>
                      <div className="themeupdate-selected-chips">
                        {selectedCategories.length === 0 ? (
                          <span className="themeupdate-selected-empty">No categories selected</span>
                        ) : (
                          selectedCategories.map((cat) => (
                            <span key={cat} className="themeupdate-selected-chip">
                              {cat}
                              <button
                                type="button"
                                className="themeupdate-chip-remove"
                                onClick={() => setSelectedCategories((prev) => prev.filter((c) => c !== cat))}
                                aria-label={`Remove ${cat}`}
                              >
                                <i className="fa-solid fa-times"></i>
                              </button>
                            </span>
                          ))
                        )}
                      </div>
                    </div>
                    <div className="themeupdate-hint-code">
                      <code>* At least 1 category must stay selected on save.
                        <br /> * Maximum {MAX_TOTAL_CATEGORIES} categories allowed.
                        <br /> * Category name up to {MAX_CATEGORY_LEN} characters.
                      </code>
                    </div>

                    <h4 className="themeupdate-label themeupdate-label-strong">Add Custom Category:</h4>
                    <div style={{ display: 'flex', gap: '10px', alignItems: 'center', marginBottom: '10px' }}>
                      <input
                        type="text"
                        className="themeupdate-input"
                        value={newCategoryName}
                        onChange={(e) => setNewCategoryName(e.target.value.slice(0, MAX_CATEGORY_LEN))}
                        maxLength={MAX_CATEGORY_LEN}
                        placeholder="Category name"
                        style={{ margin: 0 }}
                      />
                      <button
                        type="button"
                        className="themeupdate-btn themeupdate-btn-primary"
                        onClick={handleAddCustomCategory}
                        disabled={savingSelection || !newCategoryName.trim()}
                      >
                        {savingSelection ? "Adding…" : "Add Category"}
                      </button>
                    </div>
                    <div className="themeupdate-hint-code">
                      <code>* Avoid special characters. For eg. Organic Gardening but not Organic-Gardening</code>
                    </div>
                    <div className="themeupdate-actions" style={{ marginTop: '20px' }}>
                      <button
                        type="button"
                        className="themeupdate-btn themeupdate-btn-success"
                        onClick={handleSaveCategorySelection}
                        disabled={savingSelection || selectedCategories.length === 0}
                      >
                        {savingSelection ? "Saving…" : "Save Selected Categories"}
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              {/* Right Column: Category Preview and Icon Upload */}
              <div className="themeupdate-col-right">
                <div className="themeupdate-card-full">
                  <div className="themeupdate-card-header">Category Preview & Icon</div>
                  <div className="themeupdate-card-body">
                    {selectedCategories.length === 0 ? (
                      <p className="themeupdate-muted">Select categories on the left to upload icons.</p>
                    ) : (
                      <div className="themeupdate-category-preview-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '1rem' }}>
                        {selectedCategories.map((cat) => {
                          const catIconUrl =
                            data?.cat_icons && data.cat_icons[cat] ? s3PublicUrl(data.cat_icons[cat]) : null;
                          const catHeading = (catItems && catItems[cat] ? normalizeCatItem(catItems[cat]).heading : "") || "";
                          const catLabel = catHeading.trim() ? catHeading.trim() : cat;
                          return (
                            <div key={cat} className="themeupdate-category-preview-item">
                              <div className="themeupdate-preview-image">
                                {catIconUrl ? (
                                  <img src={catIconUrl} alt={cat} />
                                ) : (
                                  <div className="themeupdate-preview-placeholder">No image</div>
                                )}
                              </div>
                              <h5 className="themeupdate-preview-title">{catLabel}</h5>
                              <form
                                onSubmit={async (e) => {
                                  e.preventDefault();
                                  const file = uploadCategoryFile;
                                  if (!file) {
                                    Swal.fire("Error", "Choose a file.", "error");
                                    return;
                                  }
                                  const ext = file.name.split(".").pop().toLowerCase();
                                  if (!["png", "jpg", "jpeg"].includes(ext)) {
                                    Swal.fire("Error", "Unsupported file format. Use PNG, JPG, JPEG.", "error");
                                    return;
                                  }
                                  const valid = await validateImageDimensions(file);
                                  if (!valid) {
                                    Swal.fire("Error", "Icon image size must be 600×400.", "error");
                                    return;
                                  }
                                  const formData = new FormData();
                                  formData.append("file", file);
                                  formData.append("current_cat", cat);
                                  formData.append("currentTheme", currentTheme);
                                  setUploadingIcon(true);
                                  try {
                                    const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/uploadCategoryImage`, {
                                      method: "POST",
                                      headers: { Authorization: `Bearer ${adminToken}` },
                                      body: formData,
                                    });
                                    const result = await res.json().catch(() => ({}));
                                    if (result.success) {
                                      Swal.fire("Success", "Category icon uploaded.", "success");
                                      dispatch(fetchThemeData({ themeId: currentTheme, isAdmin: true }));
                                      setUploadCategoryFile(null);
                                    } else {
                                      Swal.fire("Error", result.error || "Upload failed", "error");
                                    }
                                  } catch {
                                    Swal.fire("Error", "Upload failed", "error");
                                  } finally {
                                    setUploadingIcon(false);
                                  }
                                }}
                                className="themeupdate-upload-form"
                              >
                                <div className="themeupdate-file-wrap">
                                  <input
                                    id={`file-upload-${cat}`}
                                    type="file"
                                    accept="image/png, image/jpeg, image/jpg"
                                    onChange={(e) => setUploadCategoryFile(e.target.files?.[0] || null)}
                                  />
                                  <label htmlFor={`file-upload-${cat}`}>Choose File</label>
                                </div>
                                <button
                                  type="submit"
                                  className="themeupdate-btn themeupdate-btn-sm themeupdate-btn-primary"
                                  disabled={uploadingIcon}
                                >
                                  {uploadingIcon ? "Uploading…" : "Upload"}
                                </button>
                              </form>
                              <p className="themeupdate-muted themeupdate-muted-sm">600×400 PNG, JPG, or JPEG</p>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div className="themeupdate-card themeupdate-card-wide">
              <div className="themeupdate-card-body">
                <p className="themeupdate-muted">
                  Categories did not load for this theme. Select a theme on Admin home and open this page again.
                </p>
              </div>
            </div>
          )}
        </div>
      )}

      {activeTab === "content" && (
        <div className="themeupdate-panel">
          {categoryEnabled ? (
            <div className="themeupdate-card themeupdate-card-wide">
              <div className="themeupdate-card-header">Category content</div>
              <div className="themeupdate-card-body">
                <div className="themeupdate-muted">

                  <div className="themeupdate-hint-code">
                    Only categories you have selected on the <strong>Categories</strong> tab appear here. Heading and info are required.
                    <code> * At least {MIN_SIDE_CHOICES} non-empty correct and {MIN_SIDE_CHOICES} non-empty incorrect choice.
                      <br />* Correct + incorrect (non-empty) together: at most {MAX_COMBINED_CHOICES} total.
                      {MAX_COMBINED_CHOICES} total.
                      <br />* Heading text: Maximum 150 characters.
                      <br />* Correct choices: Maximum 20 characters.
                      <br />* Incorrect choices: Maximum 20 characters.
                      <br />* Info text: Maximum 400 characters.
                    </code>
                  </div>
                </div>
                {categoriesForContent.length === 0 ? (
                  <p className="themeupdate-muted">Select at least one category on the Categories tab to edit content.</p>
                ) : (
                  categoriesForContent.map((cat) => {
                    const item = normalizeCatItem(catItems[cat]);
                    return (
                      <div key={cat} className="themeupdate-catitem-block">
                        <h4 className="themeupdate-catitem-title">{cat}</h4>
                        <div className="themeupdate-form-group">
                          <label className="themeupdate-label" htmlFor={`tu-h-${cat}`}>
                            Heading
                          </label>
                          <input
                            id={`tu-h-${cat}`}
                            type="text"
                            className="themeupdate-input"
                            maxLength={150}
                            value={item.heading}
                            onChange={(e) => updateCatItem(cat, { heading: e.target.value })}
                          />
                        </div>
                        <div className="themeupdate-catitem-lists">
                          <div>
                            <p className="themeupdate-label">Correct choices</p>
                            {item.correct.map((v, i) => (
                              <div key={`c-${cat}-${i}`} style={{ marginBottom: '10px' }}>
                                <div className="themeupdate-option-row" style={{ marginBottom: '5px' }}>
                                  <input
                                    type="text"
                                    className="themeupdate-input themeupdate-input-tight"
                                    maxLength={20}
                                    value={v}
                                    onChange={(e) => updateListCell(cat, "correct", i, e.target.value)}
                                    placeholder={`Correct ${i + 1}`}
                                  />
                                  <button
                                    type="button"
                                    className="themeupdate-btn themeupdate-btn-sm themeupdate-btn-danger"
                                    disabled={item.correct.length <= MIN_SIDE_CHOICES}
                                    onClick={() => removeListRow(cat, "correct", i)}
                                    aria-label="Remove row"
                                  >
                                    −
                                  </button>
                                </div>
                              </div>
                            ))}
                            <button
                              type="button"
                              className="themeupdate-btn themeupdate-btn-sm themeupdate-btn-primary"
                              disabled={item.correct.length + item.wrong.length >= MAX_COMBINED_CHOICES}
                              onClick={() => addListRow(cat, "correct")}
                            >
                              + Add correct choice
                            </button>
                          </div>
                          <div>
                            <p className="themeupdate-label">Incorrect choices</p>
                            {item.wrong.map((v, i) => (
                              <div key={`w-${cat}-${i}`} style={{ marginBottom: '10px' }}>
                                <div className="themeupdate-option-row" style={{ marginBottom: '5px' }}>
                                  <input
                                    type="text"
                                    className="themeupdate-input themeupdate-input-tight"
                                    maxLength={20}
                                    value={v}
                                    onChange={(e) => updateListCell(cat, "wrong", i, e.target.value)}
                                    placeholder={`Incorrect ${i + 1}`}
                                  />
                                  <button
                                    type="button"
                                    className="themeupdate-btn themeupdate-btn-sm themeupdate-btn-danger"
                                    disabled={item.wrong.length <= MIN_SIDE_CHOICES}
                                    onClick={() => removeListRow(cat, "wrong", i)}
                                    aria-label="Remove row"
                                  >
                                    −
                                  </button>
                                </div>
                              </div>
                            ))}
                            <button
                              type="button"
                              className="themeupdate-btn themeupdate-btn-sm themeupdate-btn-primary"
                              disabled={item.correct.length + item.wrong.length >= MAX_COMBINED_CHOICES}
                              onClick={() => addListRow(cat, "wrong")}
                            >
                              + Add incorrect choice
                            </button>
                          </div>
                        </div>
                        <div className="themeupdate-form-group">
                          <label className="themeupdate-label" htmlFor={`tu-i-${cat}`}>
                            Info
                          </label>
                          <textarea
                            id={`tu-i-${cat}`}
                            className="themeupdate-input themeupdate-textarea"
                            rows={4}
                            maxLength={400}
                            value={item.info}
                            onChange={(e) => updateCatItem(cat, { info: e.target.value })}
                          />
                        </div>
                      </div>
                    );
                  })
                )}
                <div className="themeupdate-actions">
                  <button
                    type="button"
                    className="themeupdate-btn themeupdate-btn-success"
                    onClick={handleSaveCategoryContent}
                    disabled={savingContent || categoriesForContent.length === 0}
                  >
                    {savingContent ? "Saving…" : "Save category content"}
                  </button>
                </div>
              </div>
            </div>
          ) : (
            <div className="themeupdate-card themeupdate-card-wide">
              <div className="themeupdate-card-body">
                <p className="themeupdate-muted">
                  Categories did not load for this theme. Select a theme on Admin home and open this page again.
                </p>
              </div>
            </div>
          )}
        </div>
      )}

      {activeTab === "grid" && (
        <div className="themeupdate-panel">
          <div className="themeupdate-card themeupdate-card-wide">
            <div className="themeupdate-card-header">Grid and arrow puzzle</div>
            <div className="themeupdate-card-body">
              {gridOptions.length === 0 ? (
                <p className="themeupdate-muted">No grid options are defined for this theme.</p>
              ) : (
                <>
                  <label className="themeupdate-label" htmlFor="themeupdate-grid-select">
                    Grid size
                  </label>
                  <select
                    id="themeupdate-grid-select"
                    className="themeupdate-input"
                    value={gridSelected}
                    onChange={(e) => {
                      const next = e.target.value;
                      setGridSelected(next);
                      if (!arrowPuzzleEnabled) return;
                      const { width, height } = parseGridSize(next);
                      const pk = puzzleCategoryKey ? String(puzzleCategoryKey).toUpperCase() : null;
                      const p = pk ? arrowPuzzlesByCategory?.[pk] : null;
                      const hasPuzzle = p && Array.isArray(p.paths) && p.paths.length > 0;
                      const sizeOk =
                        hasPuzzle && Number(p.width) === Number(width) && Number(p.height) === Number(height);
                      if (!pk || !hasPuzzle || !sizeOk) {
                        Swal.fire(
                          "Reminder",
                          "Grid size changed. Please click “Generate puzzle” to apply the new dimensions.",
                          "info"
                        );
                      }
                    }}
                  >
                    {gridOptions.map((opt) => (
                      <option key={String(opt)} value={String(opt)}>
                        {String(opt)}
                      </option>
                    ))}
                  </select>

                  <div className="themeupdate-toggle-row form-group-v1 toogle-row" style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
                    <label style={{ margin: 0 }}>Arrow puzzle (enable generation &amp; saving)</label>
                    <Switch checked={arrowPuzzleEnabled} onChange={setArrowPuzzleEnabled} />
                  </div>
                  {arrowPuzzleEnabled && (
                    <div className="themeupdate-puzzle-cat-block">
                      <p className="themeupdate-label themeupdate-label-strong">Puzzle category (words from Category content)</p>
                      {puzzleCatOptions.length === 0 ? (
                        <p className="themeupdate-muted themeupdate-hint-tight">
                          No ready categories yet. On <strong>Category content</strong>, add at least one correct and one
                          incorrect word for a selected category, then pick it here.
                        </p>
                      ) : (
                        <div className="themeupdate-puzzle-cat-tabs" role="tablist" aria-label="Puzzle category">
                          {puzzleCatOptions.map((cat) => (
                            <button
                              key={cat}
                              type="button"
                              role="tab"
                              aria-selected={puzzleCategoryKey === cat}
                              className={`themeupdate-puzzle-cat-tab${puzzleCategoryKey === cat ? " themeupdate-puzzle-cat-tab--active" : ""
                                }${arrowPuzzlesByCategory[cat]?.paths?.length ? " themeupdate-puzzle-cat-tab--saved" : ""}`}
                              onClick={() => setPuzzleCategoryKey(cat)}
                            >
                              {cat}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                  <div className="themeupdate-toggle-row form-group-v1 toogle-row" style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
                    <label style={{ margin: 0 }}>Wordless mode (theme flag: hide labels in the game where the theme uses it)</label>
                    <Switch
                      checked={wordlessMode}
                      onChange={(next) => {
                        setWordlessMode(next);
                        if (!arrowPuzzleEnabled) return;
                        Swal.fire(
                          "Reminder",
                          "Wordless mode changed. Please click “Generate puzzle” (and then Save) to apply this change.",
                          "info"
                        );
                      }}
                    />
                  </div>


                  <div className="themeupdate-actions themeupdate-actions--wrap">
                    <button
                      type="button"
                      className="themeupdate-btn themeupdate-btn-primary"
                      onClick={handleGenerateArrowPuzzle}
                      disabled={
                        gridLoading ||
                        generatingPuzzle ||
                        !arrowPuzzleEnabled ||
                        !puzzleCategoryKey ||
                        puzzleCatOptions.length === 0
                      }
                    >
                      {generatingPuzzle ? "Generating…" : "Generate puzzle"}
                    </button>
                    <button
                      type="button"
                      className="themeupdate-btn themeupdate-btn-success"
                      onClick={handleSaveGrid}
                      disabled={gridLoading}
                    >
                      {gridLoading ? "Saving…" : "Save grid & puzzle settings"}
                    </button>
                  </div>

                  <div className="themeupdate-arrow-preview-wrap">
                    <p className="themeupdate-label themeupdate-label-strong">Preview and label controls</p>
                    {wordlessMode && (
                      <div className="themeupdate-toggle-row form-group-v1 toogle-row" style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
                        <span style={{ flex: 1, margin: 0, fontWeight: "bold" }}>
                          Edit labels: tap one label to select (move box, rotate knob). Label size uses the side panel
                          slider (same idea as rotation), not drag-to-resize on the grid.
                        </span>
                        <Switch
                          checked={arrowPuzzleLabelEdit}
                          onChange={setArrowPuzzleLabelEdit}
                          disabled={!puzzleForPreview?.paths?.length}
                        />
                      </div>
                    )}
                    {!wordlessMode && (
                      <div className="themeupdate-actions themeupdate-actions--wrap themeupdate-actions--tight">
                        <button
                          type="button"
                          className="themeupdate-btn themeupdate-btn-sm themeupdate-btn-neutral-outline"
                          onClick={handleResetLabelPositions}
                          disabled={!puzzleForPreview?.paths?.length}
                        >
                          Reset label positions and sizes
                        </button>
                      </div>
                    )}
                    <div className="themeupdate-arrow-puzzle-layout" style={{ display: 'flex', gap: '20px', alignItems: 'flex-start', flexWrap: 'wrap' }}>
                      <div className="themeupdate-arrow-puzzle-layout__preview" style={{ flex: '1 1 auto', overflow: 'auto' }}>
                        <ArrowPuzzlePreview
                          puzzle={puzzleForPreview}
                          cellSize={previewCellSize}
                          labelEditMode={arrowPuzzleLabelEdit}
                          selectedPathIndex={arrowPuzzleSelectedLabelIndex}
                          onSelectPath={setArrowPuzzleSelectedLabelIndex}
                          onPathLabelLayoutChange={handlePathLabelLayout}
                        />
                      </div>
                      {arrowPuzzleLabelEdit && (
                        <div className="themeupdate-arrow-puzzle-layout__sidebar" style={{ flex: '0 0 320px', minWidth: '280px' }}>
                          <ArrowPuzzleLabelSidebar
                            puzzle={puzzleForPreview}
                            cellSize={previewCellSize}
                            enabled={arrowPuzzleLabelEdit}
                            selectedPathIndex={arrowPuzzleSelectedLabelIndex}
                            onSelectPath={setArrowPuzzleSelectedLabelIndex}
                            onPathLabelLayoutChange={handlePathLabelLayout}
                            onResetOnePath={handleResetOnePathLabel}
                          />
                        </div>
                      )}
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {activeTab === "addcontent" && (
        <div className="themeupdate-panel">
          <div className="themeupdate-card themeupdate-card-wide">
            <div className="themeupdate-card-header">Others configuration</div>
            <div className="themeupdate-card-body">
              <div className="themeupdate-form-group toogle-row" style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
                <label className="themeupdate-label" style={{ margin: 0, flex: 1 }}>If sound on then upload music show in admin and if false hide it</label>
                <Switch
                  checked={String(soundToggle) === "true"}
                  onChange={(checked) => setSoundToggle(checked ? "true" : "false")}
                />
              </div>

              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '30px', marginTop: '20px' }}>
                <div style={{ flex: '1 1 300px' }}>
                  <div className="themeupdate-card-full" style={{ height: '100%', marginBottom: 0 }}>
                    <div className="themeupdate-card-header">Icons (50x50 PNG)</div>
                    <div className="themeupdate-card-body" style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                      {ADD_CONTENT_KEYS.filter((k) => k.type === "icon").map((item) => (
                        <div key={item.key} style={{ border: "1px solid #e5e7eb", padding: "15px", borderRadius: "8px", backgroundColor: "#f9fafb" }}>
                          <p className="themeupdate-label themeupdate-label-strong" style={{ marginBottom: "10px" }}>{item.label}</p>
                          <div
                            style={{
                              cursor: "pointer", border: "2px dashed #9ca3af", padding: "15px",
                              display: "flex", justifyContent: "center", alignItems: "center", width: "80px", height: "80px", borderRadius: "8px",
                              backgroundColor: "#ffffff", transition: "all 0.2s"
                            }}
                            onClick={() => fileInputRefs.current[item.key]?.click()}
                          >
                            {addContentPreviews[item.key] ? (
                              <img src={addContentPreviews[item.key]} alt="" style={{ maxWidth: "100%", maxHeight: "100%", objectFit: "contain" }} />
                            ) : (
                              <div style={{ textAlign: "center", color: "#6b7280" }}>
                                <i className="fa-solid fa-cloud-arrow-up" style={{ fontSize: "20px", marginBottom: "5px" }}></i>
                                <div style={{ fontSize: "11px", fontWeight: "500" }}>Upload</div>
                              </div>
                            )}
                          </div>
                          <input
                            ref={(el) => { fileInputRefs.current[item.key] = el; }}
                            type="file"
                            accept=".png,image/png"
                            style={{ display: "none" }}
                            onChange={(e) => handleMediaFileChange(item, e)}
                          />
                          {addContentFiles[item.key] && (
                            <div style={{ fontSize: "12px", marginTop: "10px", color: "#059669", fontWeight: "500" }}>
                              <i className="fa-solid fa-check-circle" style={{ marginRight: "5px" }}></i> {addContentFiles[item.key].name}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                {String(soundToggle) === "true" && (
                  <div style={{ flex: '1 1 300px' }}>
                    <div className="themeupdate-card-full" style={{ height: '100%', marginBottom: 0 }}>
                      <div className="themeupdate-card-header">Sounds (MP3, max 10MB)</div>
                      <div className="themeupdate-card-body" style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                        {ADD_CONTENT_KEYS.filter((k) => k.type === "sound").map((item) => (
                          <div key={item.key} style={{ border: "1px solid #e5e7eb", padding: "15px", borderRadius: "8px", backgroundColor: "#f9fafb" }}>
                            <p className="themeupdate-label themeupdate-label-strong" style={{ marginBottom: "10px" }}>{item.label}</p>
                            <div
                              style={{
                                cursor: "pointer", border: "2px dashed #9ca3af", padding: "15px", display: "flex", flexDirection: "column", justifyContent: "center", alignItems: "center",
                                width: "100%", boxSizing: "border-box", borderRadius: "8px", backgroundColor: "#ffffff", transition: "all 0.2s", minHeight: "80px"
                              }}
                              onClick={() => fileInputRefs.current[item.key]?.click()}
                            >
                              {addContentPreviews[item.key] ? (
                                <audio controls src={addContentPreviews[item.key]} onClick={(ev) => ev.stopPropagation()} style={{ width: "100%", outline: "none", height: "40px" }} />
                              ) : (
                                <div style={{ textAlign: "center", color: "#6b7280" }}>
                                  <i className="fa-solid fa-file-audio" style={{ fontSize: "20px", marginBottom: "5px" }}></i>
                                  <div style={{ fontSize: "12px", fontWeight: "500" }}>Click to upload MP3</div>
                                </div>
                              )}
                            </div>
                            <input
                              ref={(el) => { fileInputRefs.current[item.key] = el; }}
                              type="file"
                              accept=".mp3,audio/mpeg,audio/mp3"
                              style={{ display: "none" }}
                              onChange={(e) => handleMediaFileChange(item, e)}
                            />
                            {addContentFiles[item.key] && (
                              <div style={{ fontSize: "12px", marginTop: "10px", color: "#059669", fontWeight: "500" }}>
                                <i className="fa-solid fa-check-circle" style={{ marginRight: "5px" }}></i> {addContentFiles[item.key].name}
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                )}
              </div>
              <div className="themeupdate-actions" style={{ marginTop: "30px", borderTop: "1px solid #e2e8f0", paddingTop: "20px", display: "flex", justifyContent: "center" }}>
                <button
                  type="button"
                  className="themeupdate-btn themeupdate-btn-success"
                  style={{ minWidth: "150px" }}
                  onClick={handleUploadAddContent}
                  disabled={mediaUploading}
                >
                  <i className="fa-solid fa-floppy-disk" style={{ marginRight: "8px" }}></i>
                  {mediaUploading ? "Saving..." : "Save"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ThemeUpdate;
